import React, { useState } from 'react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell,
  Button,
  Badge,
  Input
} from '../ui';
import { useMatchIdentifiers, useDeactivateMatchIdentifier } from '../../hooks/use-match-identifiers';
import { MatchIdentifier } from '../../types';
import { formatDate } from '../../utils';

interface MatchIdentifierTableProps {
  divisionId: string;
  onEditMatchIdentifier: (matchIdentifier: MatchIdentifier) => void;
  onTestMatchIdentifier: (matchIdentifier: MatchIdentifier) => void;
}

export function MatchIdentifierTable({ 
  divisionId, 
  onEditMatchIdentifier,
  onTestMatchIdentifier 
}: MatchIdentifierTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  
  const { 
    data: matchIdentifiersResponse, 
    isLoading, 
    error 
  } = useMatchIdentifiers(divisionId, {
    search: searchTerm || undefined,
    active: activeFilter,
  });

  const deactivateMatchIdentifier = useDeactivateMatchIdentifier();

  const handleDeactivate = async (matchIdentifier: MatchIdentifier) => {
    if (window.confirm(`Are you sure you want to deactivate "${matchIdentifier.name}"?`)) {
      try {
        await deactivateMatchIdentifier.mutateAsync({
          divisionId,
          identifierId: matchIdentifier.id,
        });
      } catch (error) {
        console.error('Failed to deactivate match identifier:', error);
      }
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveFilter(undefined);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-secondary-600 dark:text-secondary-400">Loading match identifiers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-red-600 dark:text-red-400">
          Failed to load match identifiers. Please try again.
        </div>
      </div>
    );
  }

  const matchIdentifiers = matchIdentifiersResponse?.matchIdentifiers || [];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <Input
            placeholder="Search match identifiers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
          
          <div className="flex gap-2">
            <Button
              variant={activeFilter === true ? 'primary' : 'outline'}
              onClick={() => setActiveFilter(activeFilter === true ? undefined : true)}
              size="sm"
            >
              Active Only
            </Button>
            <Button
              variant={activeFilter === false ? 'primary' : 'outline'}
              onClick={() => setActiveFilter(activeFilter === false ? undefined : false)}
              size="sm"
            >
              Inactive Only
            </Button>
          </div>
        </div>

        {(searchTerm || activeFilter !== undefined) && (
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-secondary-600 dark:text-secondary-400">
        {matchIdentifiers.length} match identifier{matchIdentifiers.length !== 1 ? 's' : ''} found
      </div>

      {/* Table */}
      {matchIdentifiers.length === 0 ? (
        <div className="text-center py-8 text-secondary-600 dark:text-secondary-400">
          {searchTerm || activeFilter !== undefined 
            ? 'No match identifiers found matching your criteria.'
            : 'No match identifiers configured yet.'
          }
        </div>
      ) : (
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {matchIdentifiers.map((matchIdentifier) => (
                <TableRow key={matchIdentifier.id}>
                  <TableCell className="font-medium">
                    {matchIdentifier.name}
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">
                      {matchIdentifier.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {matchIdentifier.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={matchIdentifier.active ? 'success' : 'default'}
                    >
                      {matchIdentifier.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {matchIdentifier.usageCount || 0} times
                  </TableCell>
                  <TableCell>
                    {matchIdentifier.lastUsed 
                      ? formatDate(matchIdentifier.lastUsed)
                      : 'Never'
                    }
                  </TableCell>
                  <TableCell>
                    {formatDate(matchIdentifier.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onTestMatchIdentifier(matchIdentifier)}
                      >
                        Test
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditMatchIdentifier(matchIdentifier)}
                      >
                        Edit
                      </Button>
                      {matchIdentifier.active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivate(matchIdentifier)}
                          disabled={deactivateMatchIdentifier.isPending}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
